import { useRef, useState, useEffect, useCallback } from 'react';

export const usePrevious = value => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = globalThis;
  return {
    width,
    height
  };
};

export const useWindowDimensions = () => {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
};

export const useUnload = fn => {
  const cb = useRef(fn); // init with fn, so that type checkers won't assume that current might be undefined

  useEffect(() => {
    cb.current = fn;
  }, [fn]);

  useEffect(() => {
    const onUnload = (...args) => cb.current?.(...args);

    window.addEventListener('beforeunload', onUnload);

    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);
};

export const useUnsavedChanges = hasUnsavedChanges => {
  // warn the user before navigating away (closing the tab, reloading, etc.)
  // if there are unsaved changes
  useUnload(
    useCallback(
      event => {
        if (!hasUnsavedChanges) return;
        event.preventDefault();
        // most browsers ignore this value and show a generic message instead,
        // but it's still required for the confirmation dialog to appear.
        event.returnValue = '';
      },
      [hasUnsavedChanges]
    )
  );
};

export default { usePrevious, useWindowDimensions, useUnload };
